(function () {
  'use strict';

  var initialized = typeof WeakSet === 'function' ? new WeakSet() : [];
  var endpoint = 'https://api.web3forms.com/submit';
  var minimumPageAge = 1500;
  // Comparison rows in display order. `key` reads the product's custom.compare_specs JSON;
  // `fallback` names the card data attribute used when a product has no compare_specs.
  var compareRows = [
    { group: 'Work area', label: 'Processing area (W × D)', key: 'processing_area', fallback: 'area' },
    { group: 'Work area', label: 'Max material height', key: 'max_height' },
    { group: 'Laser', label: 'Max laser power', key: 'max_power', fallback: 'laser' },
    { group: 'Laser', label: 'Laser sources', key: 'laser_sources' },
    { group: 'Laser', label: '10.6 µm CO₂ wattages', key: 'co2_10_6' },
    { group: 'Laser', label: '9.3 µm CO₂ wattages', key: 'co2_9_3' },
    { group: 'Laser', label: '1.06 µm fiber laser', key: 'fiber' },
    { group: 'Features', label: 'Key features', key: 'key_features', fallback: 'feature' },
    { group: 'Features', label: 'Rotary fixture (optional accessory)', key: 'rotary' },
    { group: 'Site requirements', label: 'Style and size (W × D × H)', key: 'size' },
    { group: 'Site requirements', label: 'Weight', key: 'weight' },
    { group: 'Site requirements', label: 'Electrical', key: 'electrical' },
    { group: 'Site requirements', label: 'Exhaust requirement', key: 'exhaust' },
    { group: 'Site requirements', label: 'Software and computer', key: 'software' },
    { group: 'Next steps', label: 'Spec sheet', action: 'brochure' },
    { group: 'Next steps', label: 'Request a quote', action: 'quote' }
  ];

  function hasInitialized(root) {
    return initialized instanceof WeakSet ? initialized.has(root) : initialized.indexOf(root) !== -1;
  }

  function remember(root) {
    if (initialized instanceof WeakSet) initialized.add(root);
    else initialized.push(root);
  }

  function text(value) {
    return value == null ? '' : String(value);
  }

  function clean(value) {
    return text(value).replace(/^\s+|\s+$/g, '');
  }

  function all(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function readJson(node) {
    if (!node) return {};
    try {
      var parsed = JSON.parse(node.textContent || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (ignore) {
      return {};
    }
  }

  function own(object, key) {
    return object && Object.prototype.hasOwnProperty.call(object, key) ? object[key] : null;
  }

  function safeFocus(element) {
    if (!element || typeof element.focus !== 'function') return;
    element.focus();
  }

  function scrollTo(element) {
    if (!element || typeof element.scrollIntoView !== 'function') return;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }

  function makeReference() {
    var alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    var result = '';
    var source = window.crypto && window.crypto.getRandomValues ? new Uint32Array(7) : null;
    if (source) window.crypto.getRandomValues(source);
    for (var i = 0; i < 7; i += 1) {
      result += alphabet.charAt(source ? source[i] % alphabet.length : Math.floor(Math.random() * alphabet.length));
    }
    return 'SQ-' + result;
  }

  function showDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
      dialog.setAttribute('aria-modal', 'true');
    }
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else {
      dialog.removeAttribute('open');
      dialog.removeAttribute('aria-modal');
    }
  }

  function initialize(root) {
    if (!root || hasInitialized(root)) return;
    remember(root);
    root.classList.add('usc-js-enabled');

    var form = root.querySelector('.usc-form');
    var system = form && form.querySelector('[name="system"]');
    var dialog = root.querySelector('.usc-compare-dialog');
    var tray = root.querySelector('.usc-compare-tray');
    var live = root.querySelector('.usc-live');
    var compareContent = root.querySelector('.usc-compare-content');
    var compareCount = root.querySelector('.usc-compare-count');
    var openCompare = root.querySelector('.usc-compare-open');
    var clearCompare = root.querySelector('.usc-compare-clear');
    var diffToggle = root.querySelector('.usc-compare-diff-toggle');
    var compareData = readJson(root.querySelector('script[data-usc-compare-data]'));
    // A modal dialog makes the rest of the page inert, so the live region travels into the dialog while it is open.
    var liveHome = live && live.parentNode;
    var liveNext = live && live.nextSibling;
    var success = root.querySelector('.usc-success');
    var submit = form && form.querySelector('.usc-submit');
    var status = root.querySelector('.usc-form-status');
    var genericError = root.querySelector('.usc-form-error');
    var preview = root.dataset.preview === 'true';
    var designMode = root.dataset.designMode === 'true';
    var accessKey = clean(root.dataset.web3formsKey);
    var loadedAt = Date.now();
    var inflight = false;
    var reference = '';
    var restoreFocus = null;
    var selected = [];
    var cards = {};

    all(root, '.usc-card[data-handle]').forEach(function (card) {
      cards[card.dataset.handle] = card;
    });
    if (preview) {
      var previewNotice = root.querySelector('.usc-preview-notice');
      if (previewNotice) previewNotice.hidden = false;
    }

    var seriesLinks = all(root, '.usc-series-nav [data-series-link]');
    function targetId(link) {
      var href = link.getAttribute('href') || '';
      return href.charAt(0) === '#' ? href.slice(1) : clean(link.dataset.seriesLink);
    }
    function setActiveSeries(id) {
      seriesLinks.forEach(function (link) {
        var active = targetId(link) === id;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }
    if (seriesLinks.length) {
      var hashId = window.location.hash ? window.location.hash.slice(1) : '';
      setActiveSeries(hashId || targetId(seriesLinks[0]));
      seriesLinks.forEach(function (link) {
        link.addEventListener('click', function () { setActiveSeries(targetId(link)); });
      });
      if (typeof window.IntersectionObserver === 'function') {
        var seriesTargets = seriesLinks.map(function (link) { return document.getElementById(targetId(link)); }).filter(Boolean);
        var observer = new window.IntersectionObserver(function (entries) {
          var visible = entries.filter(function (entry) { return entry.isIntersecting; });
          if (!visible.length) return;
          visible.sort(function (a, b) { return a.boundingClientRect.top - b.boundingClientRect.top; });
          setActiveSeries(visible[0].target.id);
        }, { rootMargin: '-20% 0px -65% 0px', threshold: 0.01 });
        seriesTargets.forEach(function (target) { observer.observe(target); });
        root.addEventListener('shopify:section:unload', function () { observer.disconnect(); }, { once: true });
      }
    }

    function announce(message) {
      if (live) live.textContent = message;
    }

    function optionFor(handle) {
      if (!system || !handle) return null;
      return all(system, 'option').filter(function (option) { return option.value === handle; })[0] || null;
    }

    function systemTitle(handle) {
      var option = optionFor(handle);
      var card = cards[handle];
      return clean(option && (option.dataset.title || option.textContent)) || clean(card && card.dataset.title) || 'Not sure yet';
    }

    function isUltra(handle) {
      var card = cards[handle];
      return !!card && clean(card.dataset.family).toLowerCase() === 'ultra';
    }

    function updateQuoteCopy() {
      var ultra = system && isUltra(system.value);
      var heading = root.querySelector('.usc-quote-heading');
      var intro = root.querySelector('.usc-quote-intro');
      if (heading) heading.textContent = ultra ? 'Discuss your process requirements' : 'Find the right system for your work';
      if (intro) intro.textContent = ultra
        ? 'For precision-critical R&D, medical technology, and advanced manufacturing: share your materials, feature sizes, required tolerances, and processing objectives so we can evaluate the right configuration.'
        : 'Tell us about your work and the system you are considering.';
    }

    function selectSystem(handle) {
      var option = optionFor(handle);
      if (!option) return false;
      system.value = handle;
      updateQuoteCopy();
      return true;
    }

    function ensureFormAvailable() {
      if (!form || !form.hidden) return;
      form.hidden = false;
      if (success) success.hidden = true;
      reference = '';
      if (submit) submit.disabled = !(preview || (accessKey && !designMode));
    }

    function focusQuote(handle, preserveSelection) {
      if (handle && !preserveSelection) selectSystem(handle);
      ensureFormAvailable();
      var quote = root.querySelector('[id$="-quote"]');
      scrollTo(quote || system);
      safeFocus(system);
    }

    function updateTray() {
      if (compareCount) compareCount.textContent = String(selected.length);
      if (tray) tray.hidden = selected.length === 0;
      if (openCompare) openCompare.disabled = selected.length < 2;
      all(root, '.usc-compare-check').forEach(function (checkbox) {
        checkbox.checked = selected.indexOf(checkbox.value) !== -1;
      });
    }

    function addCell(row, content, header, scope) {
      var cell = document.createElement(header ? 'th' : 'td');
      if (header) cell.scope = scope || 'row';
      cell.textContent = content;
      row.appendChild(cell);
    }

    function specsFor(handle) {
      var specs = own(own(compareData, handle), 'specs');
      return specs && typeof specs === 'object' && !Array.isArray(specs) ? specs : null;
    }

    function compareValue(handle, spec) {
      var specs = specsFor(handle);
      var card = cards[handle];
      var value = specs ? own(specs, spec.key) : (spec.fallback && card ? card.dataset[spec.fallback] : '');
      return (typeof value === 'string' || typeof value === 'number' ? clean(value) : '') || '—';
    }

    function brochureFor(handle) {
      var url = own(own(compareData, handle), 'brochure');
      url = typeof url === 'string' ? clean(url) : '';
      return /^(https?:\/\/|\/)/i.test(url) ? url : '';
    }

    function brochureCell(handle) {
      var cell = document.createElement('td');
      var url = brochureFor(handle);
      if (!url) {
        cell.textContent = '—';
        return cell;
      }
      var link = document.createElement('a');
      link.className = 'usc-compare-link';
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.setAttribute('aria-label', 'View PDF: ' + systemTitle(handle) + ' spec sheet (opens in a new tab)');
      link.appendChild(document.createTextNode('View PDF'));
      var arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = ' ↗';
      link.appendChild(arrow);
      cell.appendChild(link);
      return cell;
    }

    function quoteCell(handle) {
      var cell = document.createElement('td');
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'usc-compare-quote';
      button.dataset.system = handle;
      button.textContent = 'Request a quote';
      cell.appendChild(button);
      return cell;
    }

    function buildComparison() {
      if (!compareContent) return;
      compareContent.textContent = '';
      var table = document.createElement('table');
      table.className = 'usc-compare-table usc-compare-table--' + selected.length;
      var caption = document.createElement('caption');
      var captionText = document.createElement('span');
      captionText.textContent = 'Selected Universal Laser Systems comparison';
      caption.appendChild(captionText);
      table.appendChild(caption);
      var head = document.createElement('thead');
      var titleRow = document.createElement('tr');
      addCell(titleRow, 'System', true, 'col');
      selected.forEach(function (handle) { addCell(titleRow, systemTitle(handle), true, 'col'); });
      head.appendChild(titleRow);
      table.appendChild(head);
      var body = null;
      compareRows.forEach(function (spec) {
        if (!body || body.dataset.group !== spec.group) {
          body = document.createElement('tbody');
          body.dataset.group = spec.group;
          var groupRow = document.createElement('tr');
          var groupCell = document.createElement('th');
          groupCell.scope = 'rowgroup';
          groupCell.colSpan = selected.length + 1;
          var groupLabel = document.createElement('span');
          groupLabel.textContent = spec.group;
          groupCell.appendChild(groupLabel);
          groupRow.appendChild(groupCell);
          body.appendChild(groupRow);
          table.appendChild(body);
        }
        var row = document.createElement('tr');
        addCell(row, spec.label, true);
        if (spec.action) {
          row.className = 'usc-compare-action';
          selected.forEach(function (handle) { row.appendChild(spec.action === 'quote' ? quoteCell(handle) : brochureCell(handle)); });
        } else {
          var values = selected.map(function (handle) { return compareValue(handle, spec); });
          values.forEach(function (value) { addCell(row, value); });
          row.dataset.compareSame = values.every(function (value) { return value === values[0]; }) ? 'true' : 'false';
        }
        body.appendChild(row);
      });
      compareContent.appendChild(table);
      applyDifferences(false);
    }

    function applyDifferences(announceChange) {
      var table = compareContent && compareContent.querySelector('.usc-compare-table');
      if (!table) return;
      var only = !!(diffToggle && diffToggle.checked);
      var total = 0;
      var hidden = 0;
      all(table, 'tbody').forEach(function (body) {
        var visible = body.querySelectorAll('tr.usc-compare-action').length;
        all(body, 'tr[data-compare-same]').forEach(function (row) {
          row.hidden = only && row.dataset.compareSame === 'true';
          total += 1;
          if (row.hidden) hidden += 1;
          else visible += 1;
        });
        body.hidden = visible === 0;
      });
      if (!announceChange) return;
      var shown = total - hidden;
      if (!only) announce('Showing all ' + total + ' specification rows.');
      else if (!shown) announce('The selected systems match on every listed specification.');
      else if (!hidden) announce('All ' + total + ' specification rows differ between the selected systems.');
      else announce('Showing ' + shown + (shown === 1 ? ' row that differs; ' : ' rows that differ; ') + hidden + (hidden === 1 ? ' matching row hidden.' : ' matching rows hidden.'));
    }

    function moveLive(intoDialog) {
      if (!live || !dialog || !liveHome) return;
      if (intoDialog) {
        // Only a real modal (showModal) makes the page inert, and only then does close() fire the event that moves it back.
        if (typeof dialog.showModal === 'function') (dialog.querySelector('.usc-dialog-inner') || dialog).appendChild(live);
      } else if (live.parentNode !== liveHome) {
        liveHome.insertBefore(live, liveNext && liveNext.parentNode === liveHome ? liveNext : null);
      }
    }

    function clearComparison() {
      selected = [];
      updateTray();
      announce('Comparison cleared.');
    }

    // Return the live region right away: the dialog's close event can arrive late (e.g. while the page is not rendering).
    function dismissComparison() {
      closeDialog(dialog);
      moveLive(false);
    }

    all(root, '.usc-compare-check').forEach(function (checkbox) {
      checkbox.addEventListener('change', function () {
        var index = selected.indexOf(checkbox.value);
        if (checkbox.checked && index === -1) {
          if (selected.length === 3) {
            checkbox.checked = false;
            announce('You can compare up to three systems. Remove one before adding another.');
            return;
          }
          selected.push(checkbox.value);
          announce(systemTitle(checkbox.value) + ' added to comparison.');
        } else if (!checkbox.checked && index !== -1) {
          selected.splice(index, 1);
          announce(systemTitle(checkbox.value) + ' removed from comparison.');
        }
        updateTray();
      });
    });

    if (openCompare) openCompare.addEventListener('click', function () {
      if (selected.length < 2) {
        announce('Select at least two systems to compare.');
        return;
      }
      restoreFocus = openCompare;
      buildComparison();
      showDialog(dialog);
      moveLive(true);
      safeFocus(dialog && (dialog.querySelector('.usc-compare-close') || dialog.querySelector('button')));
    });
    if (clearCompare) clearCompare.addEventListener('click', clearComparison);
    if (diffToggle) diffToggle.addEventListener('change', function () { applyDifferences(true); });
    if (dialog) {
      var closeCompare = dialog.querySelector('.usc-compare-close');
      if (closeCompare) closeCompare.addEventListener('click', dismissComparison);
      dialog.addEventListener('close', function () {
        moveLive(false);
        safeFocus(restoreFocus);
      });
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dismissComparison();
      });
      dialog.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') dismissComparison();
      });
    }
    if (compareContent) compareContent.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('.usc-compare-quote');
      if (!button || !root.contains(button)) return;
      restoreFocus = system;
      dismissComparison();
      selectSystem(button.dataset.system);
      focusQuote(null, true);
    });
    updateTray();

    all(root, '.usc-quote-link[data-system]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        selectSystem(link.dataset.system);
        focusQuote(null, true);
      });
    });
    all(root, '[data-help-choose]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        focusQuote(null, true);
      });
    });

    if (system) system.addEventListener('change', updateQuoteCopy);
    try {
      var requested = new URLSearchParams(window.location.search).get('system');
      if (requested) selectSystem(requested);
    } catch (ignore) {}
    updateQuoteCopy();

    if (!form || !submit) return;
    submit.disabled = !(preview || (accessKey && !designMode));

    function fieldError(input) {
      if (!input) return null;
      var id = input.getAttribute('aria-describedby') || input.id + '-error';
      return root.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(id) : id.replace(/[^A-Za-z0-9_-]/g, '\\$&')));
    }

    function setFieldError(input, message) {
      var error = fieldError(input);
      input.setAttribute('aria-invalid', 'true');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    }

    function clearFieldError(input) {
      var error = fieldError(input);
      input.removeAttribute('aria-invalid');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
    }

    function showFormError(message) {
      if (genericError) {
        genericError.textContent = message;
        genericError.hidden = false;
      }
    }

    function clearFormError() {
      if (genericError) {
        genericError.hidden = true;
        genericError.textContent = '';
      }
    }

    function validate() {
      var name = form.querySelector('[name="name"]');
      var email = form.querySelector('[name="email"]');
      var first = null;
      clearFormError();
      [name, email].forEach(clearFieldError);
      if (!clean(name && name.value)) {
        setFieldError(name, 'Enter your name so we know how to address you.');
        first = name;
      }
      var emailValue = clean(email && email.value);
      if (!emailValue) {
        setFieldError(email, 'Enter your email so we can reply.');
        if (!first) first = email;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
        setFieldError(email, 'Enter a valid email address.');
        if (!first) first = email;
      }
      if (first) safeFocus(first);
      return !first;
    }

    all(form, '[name="name"], [name="email"]').forEach(function (input) {
      input.addEventListener('input', function () { clearFieldError(input); });
    });

    function appendIfPresent(data, key, value) {
      if (clean(value)) data.append(key, clean(value));
    }

    function makePayload() {
      var name = clean(form.querySelector('[name="name"]') && form.querySelector('[name="name"]').value);
      var email = clean(form.querySelector('[name="email"]') && form.querySelector('[name="email"]').value);
      var company = clean(form.querySelector('[name="company"]') && form.querySelector('[name="company"]').value);
      var phone = clean(form.querySelector('[name="phone"]') && form.querySelector('[name="phone"]').value);
      var application = clean(form.querySelector('[name="application"]') && form.querySelector('[name="application"]').value);
      var handle = clean(system && system.value);
      var title = handle ? systemTitle(handle) : 'Not sure yet';
      reference = reference || makeReference();
      var data = new window.FormData();
      data.append('access_key', accessKey);
      data.append('subject', 'ULS System Quote Request — ' + title + ' — ' + (company || name) + ' — ' + reference);
      data.append('from_name', 'Abernethy Beck — ULS System Quote');
      data.append('reference', reference);
      data.append('name', name);
      data.append('email', email);
      appendIfPresent(data, 'company', company);
      appendIfPresent(data, 'phone', phone);
      appendIfPresent(data, 'application', application);
      data.append('system', title);
      data.append('system_handle', handle);
      data.append('page_url', window.location.href);
      data.append('botcheck', '');
      return { data: data, title: title, handle: handle, reference: reference };
    }

    function honeypotHasValue(input) {
      if (!input) return false;
      if (input.type === 'checkbox' || input.type === 'radio') return input.checked;
      return !!clean(input.value);
    }

    function showSuccess(snapshot) {
      var detail = root.querySelector('.usc-success-detail');
      var referenceNode = root.querySelector('.usc-reference');
      if (detail) detail.textContent = 'Your quote request has been received for ' + snapshot.title + '.';
      if (referenceNode) referenceNode.textContent = snapshot.reference;
      form.hidden = true;
      if (success) {
        success.hidden = false;
        safeFocus(success);
        scrollTo(success);
      }
      if (!preview && !designMode) {
        try {
          if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
            window.Shopify.analytics.publish('abi_quote_submitted', {
              form_id: 'uls-system-quote', reference: snapshot.reference, model: snapshot.title, parts_count: 0, units: 0
            });
          }
        } catch (ignore) {}
      }
    }

    function request(snapshot) {
      if (preview) return Promise.resolve({ ok: true, body: { success: true } });
      var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
      var timer = controller ? window.setTimeout(function () { controller.abort(); }, 20000) : null;
      return window.fetch(endpoint, {
        method: 'POST', body: snapshot.data, headers: { Accept: 'application/json' }, signal: controller && controller.signal
      }).then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (body) {
          return { ok: response.ok, body: body };
        });
      }).finally(function () { if (timer) window.clearTimeout(timer); });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (inflight || submit.disabled) return;
      if (designMode) {
        showFormError('Submissions are disabled in the theme editor.');
        return;
      }
      if (!validate()) return;
      var honeypot = form.querySelector('[name="botcheck"]');
      if (honeypotHasValue(honeypot)) {
        showFormError('We could not send your request. Please contact us directly.');
        return;
      }
      if (Date.now() - loadedAt < minimumPageAge) {
        showFormError('Please wait a moment, then send your request.');
        return;
      }
      var snapshot = makePayload();
      inflight = true;
      submit.disabled = true;
      if (status) status.textContent = 'Sending your request…';
      request(snapshot).then(function (result) {
        if (!result.ok || !result.body || result.body.success !== true) throw new Error('Request was not accepted');
        if (status) status.textContent = '';
        showSuccess(snapshot);
      }).catch(function () {
        showFormError('We could not send your request. Please try again or contact us directly.');
        if (status) status.textContent = '';
        submit.disabled = false;
      }).finally(function () {
        inflight = false;
      });
    });

    var reset = root.querySelector('.usc-reset');
    if (reset) reset.addEventListener('click', function () {
      form.reset();
      reference = '';
      clearFormError();
      all(form, '[aria-invalid="true"]').forEach(clearFieldError);
      form.hidden = false;
      if (success) success.hidden = true;
      submit.disabled = !(preview || (accessKey && !designMode));
      updateQuoteCopy();
      safeFocus(form.querySelector('[name="name"]'));
    });
  }

  function initializeAll(scope) {
    if (scope && scope.matches && scope.matches('.usc-page')) initialize(scope);
    all(scope || document, '.usc-page').forEach(initialize);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initializeAll(document); });
  else initializeAll(document);
  document.addEventListener('shopify:section:load', function (event) { initializeAll(event.target); });
}());

/* The theme's Webflow runtime (assets/h1-custom.js) takes over same-page #links ("click.wf-scroll") and scrolls the
   target to the very top, under the pinned header. Remove that one handler so the browser scrolls natively with the
   V3 html scroll-padding-top (the same fix as uls-part-pdp.js, abi-bofa-systems.js and the other V3 pages). 2026-10-22. */
(function () {
  var off = function () { try { if (window.jQuery) window.jQuery(document).off('click.wf-scroll'); } catch (e) { /* no jQuery */ } };
  off();
  try { if (window.Webflow && typeof window.Webflow.push === 'function') window.Webflow.push(off); } catch (e) { /* no Webflow */ }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(off, 0); });
  window.addEventListener('load', off);
}());
